import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { useSessionGuard } from '@/hooks/useSessionGuard'
import type { ProfileType } from '@/types'
import FeatureJourney from '@/components/ui/FeatureJourney'

// Maps IAM roles to profile types for fallback when profileType hasn't been
// synced yet (e.g., if the profile fetch failed during login).
const ROLE_TO_PROFILE: Record<string, ProfileType> = {
  dive_center:    'DIVE_CENTER',
  travel_agency:  'TRAVEL_AGENCY',
  dive_doctor:    'DIVE_DOCTOR',
  instructor:     'INSTRUCTOR',
  divemaster:     'DIVEMASTER',
  freediver:            'FREEDIVER',
  diver:                'DIVER',
  professional_diver:   'PROFESSIONAL_DIVER',
}

interface Props {
  requireAdmin?: boolean
  /** Pannello CRM & Marketing: ruolo `sales` oppure amministratore. */
  requireSales?: boolean
  requireProfileType?: ProfileType[]
  requireRole?: string[]
}

export default function ProtectedRoute({ requireAdmin, requireSales, requireProfileType, requireRole }: Props = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isAdminUser = useAuthStore((s) => s.isAdminUser)
  const isSalesUser = useAuthStore((s) => s.isSalesUser)
  const profileType = useAuthStore((s) => s.profileType)
  const user = useAuthStore((s) => s.user)
  const location = useLocation()

  // Validates token expiry on mount and window focus; redirects to /login if stale
  useSessionGuard()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requireAdmin && !isAdminUser) {
    return <Navigate to="/app/dashboard" replace />
  }

  // Nessun FeatureJourney qui: `sales` è un incarico interno che si riceve, non un profilo
  // che si conquista. Mostrare a un subacqueo un percorso verso il pannello commerciale
  // prometterebbe una cosa che non può ottenere.
  if (requireSales && !isSalesUser) {
    return <Navigate to="/app/dashboard" replace />
  }

  if (requireProfileType) {
    // Check the stored profileType first; if null, derive from IAM roles as fallback
    const effectiveProfileType = profileType ?? (
      user?.roles?.map((r: string) => ROLE_TO_PROFILE[r]).find(Boolean) ?? null
    )
    if (!effectiveProfileType || !requireProfileType.includes(effectiveProfileType)) {
      // Niente redirect muto: mostriamo il percorso di crescita verso questa area,
      // così l'utente capisce cosa sblocca e come — non un vicolo cieco.
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-4">
          <FeatureJourney target={requireProfileType[0]} />
        </div>
      )
    }
  }

  if (requireRole && user?.roles && !requireRole.some((r) => user.roles.includes(r))) {
    // Mappa il primo ruolo richiesto a un profileType per proporre il percorso giusto.
    const targetProfile = requireRole.map((r) => ROLE_TO_PROFILE[r]).find(Boolean) ?? 'INSTRUCTOR'
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <FeatureJourney target={targetProfile} />
      </div>
    )
  }

  return <Outlet />
}
